import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Moon, Sun, Flame, Sparkles, NotebookPen, Wine } from "lucide-react";
import { VoiceNote } from "@/components/VoiceNote";
import { TriggerPicker } from "@/components/TriggerPicker";
import { NudgeBanner } from "@/components/NudgeBanner";

const MOODS = ["Grounded", "Calm", "Tender", "Anxious", "Overwhelmed"];
const FOG = [
  { v: "clear", label: "Clear" },
  { v: "mild", label: "Mild haze" },
  { v: "heavy", label: "Heavy fog" },
];

function Scale({ value, onChange, max = 5, testidPrefix, icon: Icon }) {
  return (
    <div className="flex items-center gap-2">
      {Array.from({ length: max }, (_, i) => i + 1).map((n) => {
        const active = value >= n;
        return (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            data-testid={`${testidPrefix}-${n}`}
            className="w-11 h-11 rounded-full border transition-all"
            style={{
              background: active ? "var(--clay)" : "var(--card-elev)",
              borderColor: active ? "var(--clay)" : "var(--border)",
              color: active ? "#FFFDF9" : "var(--ink-3)",
            }}
          >
            <Icon size={16} className="mx-auto" />
          </button>
        );
      })}
    </div>
  );
}

export default function CheckIn() {
  const today = new Date().toISOString().slice(0, 10);
  const [sleep, setSleep] = useState(3);
  const [energy, setEnergy] = useState(3);
  const [mood, setMood] = useState("Grounded");
  const [moodIntensity, setMoodIntensity] = useState(3);
  const [fog, setFog] = useState("clear");
  const [hotFlashes, setHotFlashes] = useState(0);
  const [note, setNote] = useState("");
  const [triggers, setTriggers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [existing, setExisting] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/checkins/today");
        if (data && data.date) {
          setExisting(data);
          setSleep(data.sleep_quality);
          setEnergy(data.energy);
          setMood(data.mood);
          setMoodIntensity(data.mood_intensity);
          setFog(data.brain_fog);
          setHotFlashes(data.hot_flashes);
          setNote(data.note || "");
          setTriggers(data.triggers || []);
        }
      } catch {}
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/checkins", {
        date: today,
        sleep_quality: sleep,
        energy,
        mood,
        mood_intensity: moodIntensity,
        brain_fog: fog,
        hot_flashes: hotFlashes,
        note,
        triggers,
      });
      toast.success("Saved to your quiet space.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto rise">
      <p className="text-xs uppercase tracking-[0.22em] text-[#8C7A70] font-semibold" data-testid="checkin-date">
        {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
      </p>
      <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl text-[#2C221E] leading-tight" data-testid="checkin-title">
        How did last night meet you?
      </h1>
      <p className="mt-3 text-[#52443E]">
        Two minutes of noticing. {existing ? "Today's entry is already saved — adjust anything that feels off." : "Nothing shared, nothing graded."}
      </p>

      <NudgeBanner />

      <div className="mt-10 space-y-8">
        <section className="card-paper p-6 sm:p-8" data-testid="checkin-sleep-section">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h2 className="font-serif-display text-xl text-[#2C221E]">Sleep quality</h2>
              <p className="text-sm text-[#73635B]">1 — broken; 5 — whole</p>
            </div>
            <Scale value={sleep} onChange={setSleep} testidPrefix="checkin-sleep" icon={Moon} />
          </div>
        </section>

        <section className="card-paper p-6 sm:p-8" data-testid="checkin-energy-section">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h2 className="font-serif-display text-xl text-[#2C221E]">Energy today</h2>
              <p className="text-sm text-[#73635B]">1 — depleted; 5 — alive</p>
            </div>
            <Scale value={energy} onChange={setEnergy} testidPrefix="checkin-energy" icon={Sun} />
          </div>
        </section>

        <section className="card-paper p-6 sm:p-8" data-testid="checkin-mood-section">
          <h2 className="font-serif-display text-xl text-[#2C221E]">Mood</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {MOODS.map((m) => (
              <button
                key={m}
                type="button"
                className="chip"
                data-active={mood === m}
                onClick={() => setMood(m)}
                data-testid={`checkin-mood-${m.toLowerCase()}`}
              >
                {m}
              </button>
            ))}
          </div>
          <div className="mt-6 flex items-center justify-between flex-wrap gap-4">
            <p className="text-sm text-[#73635B]">Intensity</p>
            <Scale value={moodIntensity} onChange={setMoodIntensity} testidPrefix="checkin-mood-intensity" icon={Sparkles} />
          </div>
        </section>

        <section className="card-paper p-6 sm:p-8" data-testid="checkin-fog-section">
          <h2 className="font-serif-display text-xl text-[#2C221E]">Brain fog</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {FOG.map((f) => (
              <button
                key={f.v}
                type="button"
                className="chip"
                data-active={fog === f.v}
                onClick={() => setFog(f.v)}
                data-testid={`checkin-fog-${f.v}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </section>

        <section className="card-paper p-6 sm:p-8" data-testid="checkin-flashes-section">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h2 className="font-serif-display text-xl text-[#2C221E]">Hot flashes & night sweats</h2>
              <p className="text-sm text-[#73635B]">Count across the last 24 hours</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="w-11 h-11 rounded-full border border-[#D8CBB9] bg-[#FFFDF9] hover:bg-[#F4EDE2]"
                onClick={() => setHotFlashes(Math.max(0, hotFlashes - 1))}
                data-testid="checkin-flashes-decrement"
              >
                −
              </button>
              <div className="min-w-[3rem] text-center font-serif-display text-2xl text-[#2C221E] flex items-center justify-center gap-2" data-testid="checkin-flashes-value">
                <Flame size={16} className="text-[#C26D53]" /> {hotFlashes}
              </div>
              <button
                type="button"
                className="w-11 h-11 rounded-full border border-[#D8CBB9] bg-[#FFFDF9] hover:bg-[#F4EDE2]"
                onClick={() => setHotFlashes(hotFlashes + 1)}
                data-testid="checkin-flashes-increment"
              >
                +
              </button>
            </div>
          </div>
        </section>

        <section className="card-paper p-6 sm:p-8" data-testid="checkin-triggers-section">
          <div className="flex items-center gap-2">
            <Wine size={16} className="text-[#8C7A70]" />
            <h2 className="font-serif-display text-xl text-[#2C221E]">What was in the mix today?</h2>
          </div>
          <p className="text-sm text-[#73635B] mt-1 mb-4">No judgement — just so Meridia can notice what tends to come before the harder days.</p>
          <TriggerPicker value={triggers} onChange={setTriggers} />
        </section>

        <section className="card-paper p-6 sm:p-8" data-testid="checkin-note-section">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div className="flex items-center gap-2">
              <NotebookPen size={16} className="text-[#8C7A70]" />
              <h2 className="font-serif-display text-xl text-[#2C221E]">A note to yourself</h2>
            </div>
            <VoiceNote onTranscript={(t) => setNote((n) => (n ? `${n.trim()}\n${t}` : t))} />
          </div>
          <textarea
            className="input-paper notebook-lines"
            rows={6}
            placeholder="What is worth remembering about today? Type it, or murmur it — up to a minute."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            data-testid="checkin-note-input"
          />
        </section>

        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            onClick={save}
            disabled={saving}
            className="btn-primary"
            data-testid="checkin-save-button"
          >
            {saving ? "Saving…" : existing ? "Update entry" : "Save entry"}
          </button>
        </div>
      </div>
    </div>
  );
}
